import axios, { AxiosInstance, AxiosError } from 'axios';
import ssoService from './ssoService';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

export type ApiErrorType = 'AUTH_FAILED' | 'FORBIDDEN' | 'EXPIRED' | 'NETWORK' | 'SERVER_ERROR';

export interface ApiError extends Error {
  type: ApiErrorType;
  code?: number | string;
  originalError?: any;
}

/**
 * API Client configuration
 * Now uses Azure AD tokens obtained via Office.js SSO
 */
class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add Azure AD auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest: any = error.config;

        // If 401 and we haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            console.log('Token expired, attempting to refresh...');
            // Get fresh token from SSO service
            const newToken = await ssoService.getAccessToken(true);
            this.setToken(newToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError: any) {
            console.error('Token refresh failed:', refreshError);
            this.clearToken();
            // Create structured error for expired token
            const structuredError = new Error(
              refreshError.message || 'Your session has expired. Please sign in again.'
            ) as ApiError;
            structuredError.type = 'EXPIRED';
            structuredError.code = 401;
            structuredError.originalError = refreshError;
            return Promise.reject(structuredError);
          }
        }

        // If 403, user might be from wrong tenant
        if (error.response?.status === 403) {
          console.error('Access forbidden - possibly wrong tenant');
          this.clearToken();
          const errorMessage = (error.response?.data as any)?.message || 
                              'You do not have permission to access this add-in. This add-in is only available for members of the authorized organization.';
          const structuredError = new Error(errorMessage) as ApiError;
          structuredError.type = 'FORBIDDEN';
          structuredError.code = 403;
          structuredError.originalError = error;
          return Promise.reject(structuredError);
        }

        // Handle network errors
        if (!error.response) {
          const structuredError = new Error(
            'Network error. Please check your internet connection and try again.'
          ) as ApiError;
          structuredError.type = 'NETWORK';
          structuredError.originalError = error;
          return Promise.reject(structuredError);
        }

        // Handle other server errors
        if (error.response?.status >= 500) {
          const structuredError = new Error(
            (error.response?.data as any)?.message || 'Server error. Please try again later.'
          ) as ApiError;
          structuredError.type = 'SERVER_ERROR';
          structuredError.code = error.response.status;
          structuredError.originalError = error;
          return Promise.reject(structuredError);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set Azure AD authentication token
   */
  setToken(token: string): void {
    this.token = token;
    // Token is already stored by ssoService, but keep this reference
  }

  /**
   * Get stored Azure AD token
   */
  getToken(): string | null {
    if (!this.token) {
      // Try to get from SSO service cache
      this.token = ssoService.getCachedToken();
    }
    return this.token;
  }

  /**
   * Clear authentication token
   */
  clearToken(): void {
    this.token = null;
    ssoService.clearToken();
    localStorage.removeItem('graph_token');
    localStorage.removeItem('graph_token_expires');
  }

  /**
   * Set Microsoft Graph token
   */
  setGraphToken(token: string, expiresOn: number): void {
    localStorage.setItem('graph_token', token);
    localStorage.setItem('graph_token_expires', expiresOn.toString());
  }

  /**
   * Get Microsoft Graph token from storage
   */
  getGraphToken(): string | null {
    const token = localStorage.getItem('graph_token');
    const expiresOn = localStorage.getItem('graph_token_expires');
    
    // Check if token is expired
    if (token && expiresOn) {
      const expiryTime = parseInt(expiresOn, 10);
      if (Date.now() < expiryTime) {
        return token;
      }
    }
    
    return null;
  }

  /**
   * Initialize Graph client - fetch and store Microsoft Graph token
   */
  async initializeGraphClient(): Promise<{ success: boolean; token?: string; message?: string }> {
    try {
      console.log('Initializing Graph client - fetching token from backend');
      // Check if we already have a valid token
      const existingToken = this.getGraphToken();
      if (existingToken) {
        return { success: true, token: existingToken };
      }

      // Fetch new token from backend
      const response = await this.client.get('/auth/graph-token');
      
      if (response.data.success && response.data.token) {
        this.setGraphToken(response.data.token, response.data.expiresOn);
        return { success: true, token: response.data.token };
      }
      
      return { success: false, message: 'Failed to fetch Graph token' };
    } catch (error: any) {
      console.error('Error initializing Graph client:', error);
      
      // If it's a 401 or auth-related error, re-throw it so App.tsx can handle it
      if (error.type === 'EXPIRED' || error.type === 'FORBIDDEN' || error.type === 'AUTH_FAILED') {
        throw error;
      }
      
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to initialize Graph client' 
      };
    }
  }

  /**
   * Initialize authentication using Azure AD SSO
   * This replaces the old email/password login
   */
  async initializeAuth(): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      // Get Azure AD token via SSO
      const token = await ssoService.getAccessToken();
      this.setToken(token);

      // Decode token to get user info (for display only)
      const userInfo = ssoService.decodeToken(token);
      console.log('✓ Authenticated as:', userInfo?.name || userInfo?.upn);

      return { success: true, token };
    } catch (error: any) {
      console.error('Authentication failed:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to authenticate with Azure AD'
      };
    }
  }

  /**
   * @deprecated Use initializeAuth() instead
   * Kept for backward compatibility but no longer functional
   */
  async login(email: string, password: string): Promise<any> {
    throw new Error('Email/password login is deprecated. The add-in now uses Azure AD SSO.');
  }

  /**
   * Match sender email with clients
   */
  async matchClient(senderEmail: string): Promise<any> {
    const response = await this.client.post('/email/match-client', { senderEmail });
    return response.data;
  }

  /**
   * Save email with attachments
   */
  async saveEmail(data: {
    clientId: number;
    email: any;
    attachments: any[];
  }): Promise<any> {
    const response = await this.client.post('/email/save', data);
    return response.data;
  }

  /**
   * Get all clients
   */
  async getClients(): Promise<any> {
    const response = await this.client.get('/clients');
    return response.data;
  }

  /**
   * Get klanten (customers) by email ID
   */
  async getKlantenByEmail(email: string): Promise<any> {
    const response = await this.client.post('/klanten/listklantenbyemailid', { email });
    return response.data;
  }

  /**
   * Save email with attachments to klanten
   */
  async saveEmailAttachments(data: {
    clientId: number;
    email: any;
    attachment: any[];
  }): Promise<any> {
    const response = await this.client.post('/klanten/saveemailattachments', data);
    return response.data;
  }

  /**
   * Get emails for a client
   */
  async getClientEmails(clientId: number, page: number = 1): Promise<any> {
    const response = await this.client.get(`/email/client/${clientId}`, {
      params: { page },
    });
    return response.data;
  }

  /**
   * Get SharePoint folder path for a client
   */
  async getClientSharePointPath(clientId: number): Promise<any> {
    const response = await this.client.get(`/klanten/sharepointpath/${clientId}`);
    console.log("SharePoint Path Response:", response);
    return response.data;
  }

  /**
   * Get available SharePoint folders for a client
   */
  async getClientSharePointFolders(clientId: number): Promise<any> {
    const response = await this.client.get(`/klanten/sharepointfolders/${clientId}`);
    console.log("SharePoint Folders Response----------------:", response);
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
